import { useState, useCallback } from 'react';
import { shareMap } from '../utils/share';
import { buildMapJson } from '../utils/mapJson';

const ENHANCE_LABELS = {
  steps: (title) => `Step per "${title}":`,
  risks: (title) => `Pro & Contro — "${title}":`,
};

export function useAIActions({
  nodes, connections, postIts, selectedNode, isLoading,
  setIsLoading, setMessages, setModalNode,
  suggestSolutions, enhanceSolution, analyzeMap, saveReport,
  createMultipleNodes, createNode, currentMapName,
}) {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [shareModal, setShareModal] = useState({ open: false, url: null, isSharing: false });

  const handleSuggestSolutions = useCallback(async (node) => {
    setModalNode(null);
    const existingConnections = connections.filter(c => c.from === node.id);
    const existingChildren = existingConnections
      .map(c => nodes.find(n => n.id === c.to))
      .filter(n => n && !n.banned);

    setMessages(prev => [...prev, {
      role: 'assistant',
      content: `Sto ragionando su "${node.title}"...`
    }]);

    try {
      const childIds = new Set(existingChildren.map(c => c.id));
      const relatedNodes = nodes.filter(n => n.id !== node.id && !childIds.has(n.id) && !n.banned);
      const solutions = await suggestSolutions(node, existingChildren, relatedNodes);
      setMessages(prev => prev.slice(0, -1));
      if (solutions.length > 0) {
        createMultipleNodes(solutions, node, nodes, connections);
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `Fatto — ${solutions.length} idee per "${node.title}".`
        }]);
      } else {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `Niente da aggiungere per "${node.title}". Prova a riformulare.`
        }]);
      }
    } catch (error) {
      setMessages(prev => prev.slice(0, -1));
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: error.message === 'DAILY_CAP_EXCEEDED'
          ? 'Hai esaurito i 2€ di AI gratuita per oggi. Torna domani — o inserisci la tua API key per continuare.'
          : `Generazione fallita: ${error.message}`
      }]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connections, nodes, suggestSolutions, createMultipleNodes]);

  const handleEnhance = useCallback(async (node, type) => {
    try {
      const text = await enhanceSolution(node, type);
      const label = ENHANCE_LABELS[type]?.(node.title) ?? type;
      setMessages(prev => [...prev, { role: 'assistant', content: `${label}\n\n${text}` }]);
    } catch (error) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `L'AI ha avuto un problema: ${error.message}`
      }]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enhanceSolution]);

  const handleAnalyzeMap = useCallback(async () => {
    if (isLoading || isAnalyzing) return;
    setIsAnalyzing(true);
    setIsLoading(true);
    try {
      const { json: mapJson, idMap } = buildMapJson(nodes, connections, selectedNode?.id);
      const createdAt = new Date().toISOString();
      const report = await analyzeMap(mapJson);
      if (!report) throw new Error('Risposta non valida dal provider.');
      const mapNodeId = (shortId) => (shortId && idMap[shortId]) ? idMap[shortId] : null;
      const enriched = {
        ...report,
        _createdAt: createdAt,
        _idMap: idMap,
        highlights: (report.highlights || []).map(h => ({
          ...h,
          nodeId: h.nodeId ?? null,
          nodeRef: mapNodeId(h.nodeId),
        })),
        actions: (report.actions || []).map(a => ({
          ...a,
          nodeId: a.nodeId ?? null,
          nodeRef: mapNodeId(a.nodeId),
        })),
      };
      saveReport(enriched);
      setReportData(enriched);
      setShowReportModal(true);
    } catch (error) {
      setMessages(prev => [...prev, { role: 'assistant', content: `Report fallito: ${error.message}` }]);
    } finally {
      setIsAnalyzing(false);
      setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes, connections, selectedNode, isLoading, isAnalyzing, analyzeMap, saveReport]);

  const handleShareMap = useCallback(async () => {
    setShareModal({ open: true, url: null, isSharing: true });
    try {
      const id = await shareMap({ nodes, connections, postIts, mapName: currentMapName });
      setShareModal({ open: true, url: `${window.location.origin}/share/${id}`, isSharing: false });
    } catch (error) {
      setShareModal({ open: false, url: null, isSharing: false });
      setMessages(prev => [...prev, { role: 'assistant', content: `Condivisione fallita: ${error.message}` }]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes, connections, postIts, currentMapName]);

  const handleCreateActions = useCallback((report) => {
    const actions = report?.actions || [];
    if (actions.length === 0) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Nessuna azione disponibile nel report.' }]);
      return;
    }

    const normalizeActionTitle = (title) => {
      if (!title) return '';
      let t = title.trim();
      const rules = [
        /^integrare\s+/i,
        /^aggiungere\s+/i,
        /^creare\s+/i,
        /^definire\s+/i,
        /^analizzare\s+/i,
        /^valutare\s+/i,
        /^mappare\s+/i,
        /^costruire\s+/i,
        /^sviluppare\s+/i,
        /^ottimizzare\s+/i,
        /^identificare\s+/i,
        /^migliorare\s+/i,
        /^ridurre\s+/i,
        /^aumentare\s+/i,
        /^misurare\s+/i,
        /^calcolare\s+/i,
        /^modellare\s+/i,
      ];
      rules.forEach((r) => {
        if (r.test(t)) t = t.replace(r, '');
      });
      t = t.replace(/^(la|il|lo|l'|un|una)\s+/i, '');
      return t.trim();
    };

    const existingTitles = new Set(nodes.map(n => n.title.trim().toLowerCase()));
    const normalizedActions = actions
      .map(a => ({ ...a, normalizedTitle: normalizeActionTitle(a.title) }))
      .filter(a => a.normalizedTitle);
    const dedupedActions = normalizedActions.filter(a => !existingTitles.has(a.normalizedTitle.toLowerCase()));
    if (dedupedActions.length === 0) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Azioni già presenti come nodi.' }]);
      return;
    }

    const resolveParent = (action) => {
      const nodeId = action?.nodeRef
        || (action?.nodeId && report?._idMap?.[action.nodeId])
        || selectedNode?.id
        || nodes.find(n => n.type === 'problem')?.id
        || null;
      return nodeId ? nodes.find(n => n.id === nodeId) : null;
    };

    const groups = new Map();
    dedupedActions.forEach(action => {
      const parent = resolveParent(action);
      const key = parent ? parent.id : '__root__';
      if (!groups.has(key)) groups.set(key, { parent, items: [] });
      groups.get(key).items.push(action);
    });

    groups.forEach(({ parent, items }) => {
      let parentNode = parent;
      let baseNodes = nodes;
      let baseConnections = connections;
      if (!parentNode) {
        parentNode = createNode(
          { type: 'problem', title: "Piano d'azione", summary: 'Azioni generate dal report' },
          nodes,
          connections
        );
        baseNodes = [...nodes, parentNode];
      }
      const solutions = items.map(a => ({ title: a.normalizedTitle || a.title, summary: a.why || '' }));
      createMultipleNodes(solutions, parentNode, baseNodes, baseConnections);
    });

    setMessages(prev => [...prev, { role: 'assistant', content: `Azioni create: ${dedupedActions.length}` }]);
  }, [nodes, connections, selectedNode, setMessages, createMultipleNodes, createNode]);

  return {
    isAnalyzing,
    reportData,
    setReportData,
    showReportModal,
    setShowReportModal,
    shareModal,
    setShareModal,
    handleSuggestSolutions,
    handleEnhance,
    handleAnalyzeMap,
    handleShareMap,
    handleCreateActions,
  };
}
